import React, { useState, useEffect } from 'react';
import WaterfallChart from '@/components/charts/WaterfallChart';
import { useTheme } from '@/contexts/ThemeContext';

interface InventoryChangeViewProps {
  data: any[];
  inventoryChangeKey?: string;
  inventoryChangeCumulativeKey?: string;
  mode?: 'total' | 'cumulative';
  onModeChange?: (mode: 'total' | 'cumulative') => void;
  polymer?: 'PE' | 'PP' | 'PS' | 'PVC';
  fullBleed?: boolean;
}

const InventoryChangeView: React.FC<InventoryChangeViewProps> = ({
  data,
  inventoryChangeKey = 'inventory_change_monthly',
  inventoryChangeCumulativeKey = 'inventory_change_cumulative',
  mode: externalMode,
  onModeChange,
  polymer,
  fullBleed = false,
}) => {
  const [internalMode, setInternalMode] = useState<'total' | 'cumulative'>('total');
  const mode = externalMode ?? internalMode;
  const setMode = onModeChange ?? setInternalMode;
  const { isDark } = useTheme();

  // Below 1024px, hide chart data labels.
  const [hideDataLabels, setHideDataLabels] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches,
  );

  // Below 749px, center the title and buttons.
  const [isSmallTitle, setIsSmallTitle] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 749px)').matches,
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    const smallTitleMq = window.matchMedia('(max-width: 749px)');
    const handler = (e: MediaQueryListEvent) => setHideDataLabels(e.matches);
    const smallTitleHandler = (e: MediaQueryListEvent) => setIsSmallTitle(e.matches);
    mq.addEventListener('change', handler);
    smallTitleMq.addEventListener('change', smallTitleHandler);
    return () => {
      mq.removeEventListener('change', handler);
      smallTitleMq.removeEventListener('change', smallTitleHandler);
    };
  }, []);

  // Keep only the first entry per month, drop any same-month duplicates.
  const seenMonths = new Set<string>();
  const allSorted = [...data]
    .sort((a, b) => (a.date || a.record_date) < (b.date || b.record_date) ? -1 : 1)
    .filter((d) => {
      const ym = String(d.date || d.record_date).slice(0, 7);
      if (seenMonths.has(ym)) return false;
      seenMonths.add(ym);
      return true;
    });

  // PE/PP 14 months (final date − 13 months), PS/PVC: 13 months (final date − 12 months)
  // and re-base so the baseline month starts at 0.
  let sorted = allSorted;
  if (mode === 'cumulative' && allSorted.length > 0) {
    const monthsToShow = (polymer === 'PS' || polymer === 'PVC') ? 12 : 13;
    const finalDate = String(allSorted[allSorted.length - 1].date || allSorted[allSorted.length - 1].record_date);
    const [yStr, mStr] = finalDate.split('-');
    let y = Number(yStr);
    let m = Number(mStr) - monthsToShow;
    while (m <= 0) {
      m += 12;
      y -= 1;
    }
    const baselineYM = `${y}-${String(m).padStart(2, '0')}`;
    sorted = allSorted.filter((d) => String(d.date || d.record_date).slice(0, 7) >= baselineYM);
  }

  const labels = sorted.map((d) => String(d.date || d.record_date).slice(0, 10));

  // WaterfallChart accumulates internally for cumulative mode, so we always feed it
  // monthly deltas. For cumulative, the baseline month (index 0) is forced to 0 so the
  // running total starts at 0 and grows by each subsequent month's change.
  const values = sorted.map((d, index) => {
    if (mode === 'cumulative' && index === 0) return 0;
    const v = d[inventoryChangeKey];
    return v !== null && v !== undefined ? Number(v) : 0;
  });

  return (
    <div className={`flex flex-col gap-4 ${fullBleed && isSmallTitle ? '-mx-6 p-0 px-6' : ''}`}>
      {!onModeChange && (
        <div className={`flex gap-2 ${isSmallTitle ? 'justify-center' : 'ml-auto'}`}>
          <button
            onClick={() => setMode('total')}
            className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
              mode === 'total' ? 'bg-rs-green text-black' : 'bg-rs-jet text-rs-off-white/70 hover:bg-rs-jet-light'
            }`}
          >
            Total Change
          </button>
          <button
            onClick={() => setMode('cumulative')}
            className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
              mode === 'cumulative' ? 'bg-rs-green text-black' : 'bg-rs-jet text-rs-off-white/70 hover:bg-rs-jet-light'
            }`}
          >
            Cumulative
          </button>
        </div>
      )}

      <div className={`border border-rs-jet-light rounded-lg ${fullBleed && isSmallTitle ? 'p-2 -mx-6 px-6' : 'p-4'} ${isDark ? 'bg-rs-off-black/40' : 'bg-rs-off-black'}`}>
        <h3 className={`text-rs-off-white font-medium text-sm mb-3 ${isSmallTitle ? 'text-center' : ''}`}>
          Inventory Change — {mode === 'cumulative' ? 'Cumulative' : 'Monthly'}
        </h3>
        <p className={`text-rs-off-white/50 text-sm mb-3 ${isSmallTitle ? 'text-center' : ''}`}>Million lbs</p>
        {data.length === 0 ? (
          <div className="h-[500px] flex items-center justify-center text-rs-off-white/40 text-sm">No data</div>
        ) : (
          <div className="h-[500px]">
            <WaterfallChart data={values} labels={labels} mode={mode} hideDataLabels={hideDataLabels} className={`w-full h-full rounded ${isDark ? 'bg-rs-jet' : 'bg-transparent'}`} />
          </div>
        )}
      </div>
      <p className={`mt-4 text-rs-highlight-gray text-xs ${isSmallTitle && !fullBleed ? 'px-4' : ''}`}>Source: American Chemistry Council® (ACC)</p>
    </div>
  );
};

export default InventoryChangeView;
